import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasAiApi } from '~/services/kyClient';

import { log } from '~/utils';

import {
  type UploadPdfToAiProjectArguments,
  type UploadPdfToAiProjectResponse,
} from './types';

/**
 * Uploads a PDF file to the training data of an AI project.
 * @param arguments - The upload arguments
 * @param arguments.pdfFile - The PDF file to upload
 * @param arguments.projectName - The name of the AI project to upload the PDF file to
 * @param options - Optional request options
 * @returns A promise that resolves with the API response
 * @see https://fa-vestigas-dev.azurewebsites.net/docs#/Metadata/upload_pdf_to_blob_upload_pdf_to_blob_post
 */
export const uploadPdfToAiProject = async (
  { pdfFile, projectName }: UploadPdfToAiProjectArguments,
  options?: KyRequestOptions,
) => {
  try {
    const formData = new FormData();
    formData.append('files', pdfFile);

    const response = await vestigasAiApi
      .post(ENDPOINT.AI.UPLOAD_PDF_TO_BLOB(), {
        body: formData,
        searchParams: {
          projectName,
        },
        validate: {
          searchParams: z.object({
            projectName: z.string().min(1),
          }),
        },
        ...options,
      })
      .json<UploadPdfToAiProjectResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error uploading PDF file to AI project',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
